import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit, Trash2 } from 'lucide-react';

const Designs = () => {
    const [designs, setDesigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingDesign, setEditingDesign] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        category: 'logo',
        imageUrl: '',
        price: '',
        tags: '',
        featured: false
    });

    const fetchDesigns = useCallback(async () => {
        try {
            const response = await fetch('/api/designs');
            const data = await response.json();
            setDesigns(data);
        } catch (error) {
            console.error('Tasarımlar yüklenirken hata:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchDesigns();
    }, [fetchDesigns]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const url = editingDesign ? `/api/designs/${editingDesign._id}` : '/api/designs';
            const method = editingDesign ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    ...formData,
                    tags: formData.tags.split(',').map(tag => tag.trim())
                }),
            });

            if (response.ok) {
                fetchDesigns();
                setShowModal(false);
                resetForm();
            }
        } catch (error) {
            console.error('Tasarım kaydedilirken hata:', error);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Bu tasarımı silmek istediğinizden emin misiniz?')) {
            try {
                await fetch(`/api/designs/${id}`, { method: 'DELETE' });
                fetchDesigns();
            } catch (error) {
                console.error('Tasarım silinirken hata:', error);
            }
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            description: '',
            category: 'logo',
            imageUrl: '',
            price: '',
            tags: '',
            featured: false
        });
        setEditingDesign(null);
    };

    const openModal = (design = null) => {
        if (design) {
            setFormData({
                ...design,
                tags: design.tags.join(', ')
            });
            setEditingDesign(design);
        } else {
            resetForm();
        }
        setShowModal(true);
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    return (
        <div className="p-6">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-900">Tasarımlar</h1>
                <button
                    onClick={() => openModal()}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"
                >
                    <Plus className="h-5 w-5" />
                    Yeni Tasarım
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {designs.map((design) => (
                    <div key={design._id} className="bg-white rounded-lg shadow-md overflow-hidden">
                        <div className="h-48 bg-gray-200 relative">
                            {design.imageUrl ? (
                                <img
                                    src={design.imageUrl}
                                    alt={design.name}
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <div className="flex items-center justify-center h-full text-gray-500">
                                    Görsel Yok
                                </div>
                            )}
                            {design.featured && (
                                <span className="absolute top-2 right-2 bg-yellow-500 text-white px-2 py-1 rounded text-xs">
                                    Öne Çıkan
                                </span>
                            )}
                        </div>

                        <div className="p-4">
                            <h3 className="font-semibold text-lg mb-2">{design.name}</h3>
                            <p className="text-gray-600 text-sm mb-2">{design.description}</p>
                            <div className="flex justify-between items-center mb-3">
                                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                                    {design.category}
                                </span>
                                <span className="font-bold text-green-600">
                                    ₺{design.price}
                                </span>
                            </div>

                            <div className="flex flex-wrap gap-1 mb-3">
                                {design.tags.map((tag, index) => (
                                    <span key={index} className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                                        {tag}
                                    </span>
                                ))}
                            </div>

                            <div className="flex justify-between">
                                <button
                                    onClick={() => openModal(design)}
                                    className="text-blue-500 hover:text-blue-700"
                                >
                                    <Edit className="h-5 w-5" />
                                </button>
                                <button
                                    onClick={() => handleDelete(design._id)}
                                    className="text-red-500 hover:text-red-700"
                                >
                                    <Trash2 className="h-5 w-5" />
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {designs.length === 0 && (
                <div className="text-center py-12">
                    <p className="text-gray-500 text-lg">Henüz tasarım eklenmemiş</p>
                </div>
            )}

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
                        <h2 className="text-xl font-bold mb-4">
                            {editingDesign ? 'Tasarımı Düzenle' : 'Yeni Tasarım'}
                        </h2>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Tasarım Adı
                                </label>
                                <input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Açıklama
                                </label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                    rows="3"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Kategori
                                </label>
                                <select
                                    value={formData.category}
                                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                >
                                    <option value="logo">Logo</option>
                                    <option value="banner">Banner</option>
                                    <option value="poster">Poster</option>
                                    <option value="kartvizit">Kartvizit</option>
                                    <option value="sosyal-medya">Sosyal Medya</option>
                                    <option value="web-tasarim">Web Tasarım</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Görsel URL
                                </label>
                                <input
                                    type="url"
                                    value={formData.imageUrl}
                                    onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Fiyat (₺)
                                </label>
                                <input
                                    type="number"
                                    value={formData.price}
                                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Etiketler (virgülle ayırın)
                                </label>
                                <input
                                    type="text"
                                    value={formData.tags}
                                    onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                    placeholder="modern, minimalist, profesyonel"
                                />
                            </div>

                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="featured"
                                    checked={formData.featured}
                                    onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                                    className="mr-2"
                                />
                                <label htmlFor="featured" className="text-sm font-medium text-gray-700">
                                    Öne Çıkan Tasarım
                                </label>
                            </div>

                            <div className="flex gap-3 pt-4">
                                <button
                                    type="submit"
                                    className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-2 rounded-lg"
                                >
                                    {editingDesign ? 'Güncelle' : 'Kaydet'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 py-2 rounded-lg"
                                >
                                    İptal
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Designs;